import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { pickBackground } from "@/lib/backgrounds";

/**
 * Háttér: véletlenszerű háttérkép (szervezeti vagy beépített); betöltésig és kép hiányában
 * sötét, a kísérőszín mély árnyalatából induló felület, rajta a márkaszínek izzó fényfoltjaival
 * és finom szemcsézettel.
 */
export function Backdrop({ urls, className, dim = true }: { urls?: string[]; className?: string; dim?: boolean }) {
  const [seed] = useState(() => Math.random());
  const url = useMemo(() => pickBackground(urls, seed), [urls, seed]);
  const [loaded, setLoaded] = useState(false);

  return (
    <div className={cn("absolute inset-0 overflow-hidden bg-soft-950", className)} aria-hidden>
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_0%_0%,oklch(var(--soft-900))_0%,transparent_60%)]" />
      <div className="absolute -left-[12%] -top-[18%] h-[65%] w-[65%] animate-float rounded-full bg-brand-500/70 blur-[110px]" />
      <div className="absolute -bottom-[22%] -right-[12%] h-[75%] w-[70%] animate-float rounded-full bg-soft-500/60 blur-[120px] [animation-delay:-6s]" />
      <div className="absolute left-[28%] top-[38%] h-[40%] w-[40%] animate-float rounded-full bg-brand-700/60 blur-[100px] [animation-delay:-11s]" />
      <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay [background-image:radial-gradient(oklch(100%_0_0)_1px,transparent_1px)] [background-size:3px_3px]" />
      {url && (
        <img
          src={url}
          alt=""
          onLoad={() => setLoaded(true)}
          className={cn("absolute inset-0 h-full w-full scale-105 object-cover transition-all duration-[1.4s] ease-out", loaded ? "scale-100 opacity-100" : "opacity-0")}
        />
      )}
      {dim && url && <div className="absolute inset-0 bg-gradient-to-t from-[oklch(20%_0.04_285/0.55)] via-transparent to-[oklch(20%_0.04_285/0.15)]" />}
    </div>
  );
}

/** Munkamenetenként állandó választás, hogy oldalváltáskor ne cserélődjön a kép. */
function sessionSeed(): number {
  try {
    const k = "propsys.bgSeed";
    const v = sessionStorage.getItem(k);
    if (v) return Number(v);
    const n = Math.random();
    sessionStorage.setItem(k, String(n));
    return n;
  } catch {
    return Math.random();
  }
}

/**
 * Tartalmi oldalak háttere asztalon: ugyanazok a képek, nagyon halványan, a tartalom mögött.
 * Mobilon nem jelenik meg (kicsi a felület, és csak a betöltést lassítaná).
 */
export function PageBackdrop({ urls }: { urls?: string[] }) {
  const [seed] = useState(sessionSeed);
  const url = useMemo(() => pickBackground(urls, seed), [urls, seed]);
  const [loaded, setLoaded] = useState(false);
  if (!url) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-0 hidden lg:block" aria-hidden>
      <img
        src={url}
        alt=""
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={cn("h-full w-full object-cover grayscale-[35%] transition-opacity duration-1000", loaded ? "opacity-[0.07]" : "opacity-0")}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-transparent to-background/80" />
    </div>
  );
}
